import { FC } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { Button } from '@app/ui'
import { ButtonSize, ButtonType } from '@app/types'
import Portal from '@app/ui/Modal/Portal'

interface SupportRequestSuccessModalProps {
  open: boolean
  onAction: () => void
}

const SupportRequestSuccessModal: FC<SupportRequestSuccessModalProps> = ({ open, onAction }) => {
  const { t } = useTranslation()

  if (!open) return null

  return (
    <Portal>
      <StyledOverlay>
        <StyledCard>
          <StyledCheckBadge>
            <StyledCheck />
          </StyledCheckBadge>

          <StyledTitle>{t('supportRequest.success.title')}</StyledTitle>

          <Button
            size={ButtonSize.big}
            fluid
            type={ButtonType.buttonColor}
            onClick={onAction}
            textColor="var(--r-color-white)"
            borderRadius="8px"
          >
            {t('supportRequest.success.goToFeed')}
          </Button>
        </StyledCard>
      </StyledOverlay>
    </Portal>
  )
}

const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10050;
  padding: 0 28px;
  max-width: 600px;
  margin: 0 auto;
  background: rgba(8, 8, 12, 0.58);
  backdrop-filter: blur(8px);
`

const StyledCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  max-width: min(390px, calc(100vw - 56px));
  box-sizing: border-box;
  padding: 38px 20px 16px;
  border-radius: 12px;
  overflow: hidden;
  background: var(--bg-quaternary);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.42);
  transform: translate(-50%, -50%);

  &::before {
    content: '';
    position: absolute;
    top: -190px;
    left: 50%;
    transform: translateX(-50%);
    width: 360px;
    height: 360px;
    border-radius: 50%;
    pointer-events: none;
    background:
      radial-gradient(
        circle,
        rgba(140, 0, 255, 0.3) 0%,
        rgba(110, 0, 255, 0.14) 34%,
        rgba(80, 0, 180, 0.04) 58%,
        transparent 76%
      );
    filter: blur(34px);
  }

  [data-theme="light"] &::before {
    background:
      radial-gradient(
        circle,
        rgba(140, 0, 255, 0.12) 0%,
        rgba(110, 0, 255, 0.06) 34%,
        transparent 58%,
        transparent 76%
      );
  }

  > * {
    position: relative;
    z-index: 1;
  }

  .Button {
    height: 52px;
    font-size: 16px;
    font-weight: 600;
  }
`

const StyledCheckBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  margin-bottom: 28px;
  border-radius: 50%;
  background: rgba(var(--violet-600-rgb), 0.24);
  border: 1px solid rgba(var(--violet-500-rgb), 0.7);
  box-shadow:
    0 0 28px rgba(var(--violet-600-rgb), 0.6),
    inset 0 0 18px rgba(var(--violet-600-rgb), 0.24);
`

const StyledCheck = styled.span`
  position: relative;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--violet-400, #8B5CF6);

  &::after {
    content: '';
    position: absolute;
    left: 8px;
    top: 7px;
    width: 10px;
    height: 6px;
    border-left: 3px solid #18181B;
    border-bottom: 3px solid #18181B;
    transform: rotate(-45deg);
  }

  [data-theme="light"] &::after {
    border-left: 3px solid #FFFFFF;
    border-bottom: 3px solid #FFFFFF;
  }
`

const StyledTitle = styled.h2`
  max-width: 285px;
  margin: 0 0 28px;
  color: var(--text-primary, #FAFAFA);
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  text-align: center;
`

export default SupportRequestSuccessModal
